// Phase 18 Plan 04 Task 1 — POLISH-07 useAnnounce hook.
//
// Returns a function (text, mode?) => void that writes textContent to the
// matching <LiveRegion> child div. 100ms trailing-edge debounce per CONTEXT
// Claude's Discretion (finalized) so rapid-fire match events don't trample
// each other. Clear-then-set pattern forces re-announce of identical text
// (RESEARCH §Pitfall 4).
//
// The pending-timer map lives at module scope so multiple components calling
// useAnnounce() share the same debouncer (single SR output stream). This
// matches the global-region invariant — there is only one polite and one
// assertive live region in the DOM at any given moment.

const DEBOUNCE_MS = 100

type Mode = 'polite' | 'assertive'

const pending: {
  polite: ReturnType<typeof setTimeout> | null
  assertive: ReturnType<typeof setTimeout> | null
} = { polite: null, assertive: null }

export function useAnnounce(): (text: string, mode?: Mode) => void {
  return (text: string, mode: Mode = 'polite') => {
    const id = mode === 'assertive' ? 'rg-live-assertive' : 'rg-live-polite'
    const region = document.getElementById(id)
    if (!region) return // <LiveRegion> not mounted (e.g. mid-test boundary)

    const existing = pending[mode]
    if (existing !== null) clearTimeout(existing)
    pending[mode] = setTimeout(() => {
      // Clear-then-set: forces SR to re-announce identical text. RESEARCH
      // §Pitfall 4 — without this, "Your turn." → "Your turn." is silent on
      // most screen readers because textContent didn't change.
      region.textContent = ''
      region.textContent = text
      pending[mode] = null
    }, DEBOUNCE_MS)
  }
}
